import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { bizApi } from "../lib/bizApi";
import type { LoanStatus } from "../lib/loanApi";
import type { LeaveStatus } from "../lib/leaveApi";
import type { TaskStatus } from "../lib/taskApi";
import type { AttendanceStatus } from "../lib/attendanceApi";

export const BIZ_KEYS = {
  all: (bizId: string) => ["biz", bizId] as const,
  users: (bizId: string) => ["biz", bizId, "users"] as const,
  loans: (bizId: string, status?: LoanStatus) =>
    ["biz", bizId, "loans", status ?? "ALL"] as const,
  leaves: (bizId: string, status?: LeaveStatus) =>
    ["biz", bizId, "leaves", status ?? "ALL"] as const,
  tasks: (bizId: string, status?: TaskStatus) =>
    ["biz", bizId, "tasks", status ?? "ALL"] as const,
  attendance: (bizId: string, date?: string) =>
    ["biz", bizId, "attendance", date ?? "ALL"] as const,
};

const Q = { staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: true } as const;

// ── Users ──────────────────────────────────────────────────────────────────

export function useBizUsers(businessId: string) {
  return useQuery({
    queryKey: BIZ_KEYS.users(businessId),
    queryFn: () => bizApi.listUsers(businessId).then((r) => r.items),
    enabled: !!businessId,
    ...Q,
  });
}

// ── Loans ──────────────────────────────────────────────────────────────────

export function useBizLoans(businessId: string, status?: LoanStatus) {
  return useQuery({
    queryKey: BIZ_KEYS.loans(businessId, status),
    queryFn: () => bizApi.listLoans(businessId, status).then((r) => r.items),
    enabled: !!businessId,
    ...Q,
  });
}

export function useBizApproveLoan(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, userId }: { loanId: string; userId: string }) =>
      bizApi.approveLoan(businessId, loanId, userId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIZ_KEYS.all(businessId) });
    },
  });
}

export function useBizRejectLoan(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      userId,
      reason,
    }: {
      loanId: string;
      userId: string;
      reason: string;
    }) => bizApi.rejectLoan(businessId, loanId, userId, reason),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIZ_KEYS.all(businessId) });
    },
  });
}

export function useBizReleaseLoan(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      userId,
      releaseDate,
      disbursementAccount,
    }: {
      loanId: string;
      userId: string;
      releaseDate: string;
      disbursementAccount?: string;
    }) => bizApi.releaseLoan(businessId, loanId, userId, releaseDate, disbursementAccount),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIZ_KEYS.all(businessId) });
    },
  });
}

// ── Leaves ─────────────────────────────────────────────────────────────────

export function useBizLeaves(businessId: string, status?: LeaveStatus) {
  return useQuery({
    queryKey: BIZ_KEYS.leaves(businessId, status),
    queryFn: () => bizApi.listLeaves(businessId, status).then((r) => r.items),
    enabled: !!businessId,
    ...Q,
  });
}

export function useBizApproveLeave(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      leaveId,
      userId,
      note,
    }: {
      leaveId: string;
      userId: string;
      note?: string;
    }) => bizApi.approveLeave(businessId, leaveId, userId, note),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIZ_KEYS.all(businessId) });
    },
  });
}

export function useBizRejectLeave(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      leaveId,
      userId,
      note,
    }: {
      leaveId: string;
      userId: string;
      note?: string;
    }) => bizApi.rejectLeave(businessId, leaveId, userId, note),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIZ_KEYS.all(businessId) });
    },
  });
}

// ── Tasks ──────────────────────────────────────────────────────────────────

export function useBizTasks(businessId: string, status?: TaskStatus) {
  return useQuery({
    queryKey: BIZ_KEYS.tasks(businessId, status),
    queryFn: () => bizApi.listTasks(businessId, status).then((r) => r.items),
    enabled: !!businessId,
    ...Q,
  });
}

export function useBizCreateTask(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      assignedTo: string;
      title: string;
      description: string;
      priority: string;
      dueDate?: string;
    }) => bizApi.createTask(businessId, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIZ_KEYS.all(businessId) });
    },
  });
}

// ── Attendance ─────────────────────────────────────────────────────────────

export function useBizAttendance(businessId: string, date?: string) {
  return useQuery({
    queryKey: BIZ_KEYS.attendance(businessId, date),
    queryFn: () => bizApi.listAttendance(businessId, date).then((r) => r.items),
    enabled: !!businessId,
    ...Q,
  });
}

export function useBizUpsertAttendance(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      userId: string;
      employeeName?: string;
      date: string;
      status: AttendanceStatus;
      timeIn?: string;
      timeOut?: string;
      notes?: string;
    }) => bizApi.upsertAttendance(businessId, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIZ_KEYS.all(businessId) });
    },
  });
}
